import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  ChartOptions,
  ChartDataset,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import React, { useState } from 'react';
import { UseQueryResult } from '@tanstack/react-query';
import moment from 'moment';
import {
  dataPointLogic,
  dataSet,
} from '@components/progress/chart/ChartHelper';
import { topicSkillLevel } from '@components/progress/sheet/SelectedTopic';
import NotAvailableState from '@components/progress/chart/NotAvailableState';
import { ITableItemSkillLevel } from '@components/progress/table/TableItem';
import { masteryGraphRandomizer } from '@components/progress/MasteryTopicRandomizer';
import { IMasteryProgress } from '@models/interfaces/student.interface';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
);

export interface LineChartMasteryProps {
  xAxisLabel: string[];
  rawData: UseQueryResult<IMasteryProgress[]>;
  rangeSelected: string | number;
  selectedTopics: topicSkillLevel[];
  isLoading: boolean;
  isTestAccount: boolean;
  topics: ITableItemSkillLevel[];
}

const LineChartMasteryLevel = (props: LineChartMasteryProps) => {
  const now = moment();
  const options: ChartOptions<'line'> = {
    scales: {
      y: {
        position: 'right',
        suggestedMax: 10,
        suggestedMin: 0,
        title: {
          text: 'Skill Level',
          display: true,
          color: '#FAFBFD',
          padding: {
            bottom: -5,
          },
        },
        grid: {
          color: '#FAFBFD4C',
        },
        ticks: {
          color: '#FAFBFD',
          stepSize: 2,
        },
      },
      x: {
        grid: {
          color: '#FAFBFD4C',
        },
        border: {
          dash: [5, 5],
        },
        ticks: {
          color: '#FAFBFD',
          autoSkip: false,
          callback: function (tickValue, index) {
            return dataPointLogic(index, props.rangeSelected)
              ? this.getLabelForValue(index)
              : null;
          },
        },
      },
    },
    elements: {
      point: {
        radius: ctx =>
          dataPointLogic(ctx.dataIndex, props.rangeSelected) ? 4 : 0,
      },
    },
  };

  const [fakeData, setFakeData] = useState<{ [key: string]: number[] }>({});

  // data transformation
  const chartData: ChartDataset<'line', number[]>[] = [];
  let haveValidDataToPlot = false;
  if (props.rawData.data && !props.isTestAccount) {
    for (let i = 0; i < props.rawData.data.length; i++) {
      const date = now.clone().subtract(props.rangeSelected, 'days');
      const data = props.rawData.data[i]?.date_list;
      const topicId: number = props.rawData.data[i]?.topic_id;
      const dataToPush: number[] = [];
      if (data.length > 0) {
        let lastScore = NaN;
        let rangeSelected: number;
        if (typeof props.rangeSelected === 'string') {
            rangeSelected = parseInt(props.rangeSelected);
        } else {
            rangeSelected = props.rangeSelected;
        }
        for (let j = 0; j <= rangeSelected; j++) {
          const identifier = date.format('YYYY-MM-DD');
          const searched = data.filter(
            value => value.datetime_of_mast_cal.search(identifier) > -1,
          );
          if (searched.length > 0) {
            haveValidDataToPlot = true;
            lastScore = searched[0].student_score
              ? searched[0].student_score
              : searched[0].average_student_score ?? 0;
          }
          dataToPush.push(lastScore);
          date.add(1, 'day');
        }
      }
      const color =
        props.selectedTopics.filter(item => item.id == topicId)[0]?.color ??
        '#D5ACF3';
      const label =
        props.selectedTopics.filter(item => item.id == topicId)[0]?.label ??
        'Total';
      chartData.push(dataSet(dataToPush, color, label));
    }
  }

  function getDataPoints(index: number | string, range: number, score: number) {
    let dataPoints: number[];

    if (fakeData[index + '_' + range]) {
      dataPoints = fakeData[index + '_' + range];
    } else {
      dataPoints = masteryGraphRandomizer(range, score);
      const fakeDataNew = fakeData;
      fakeDataNew[index + '_' + range] = dataPoints;
      setFakeData(fakeDataNew);
    }
    return dataPoints;
  }

  if (props.isTestAccount) {
    const range =
      typeof props.rangeSelected === 'string'
        ? parseInt(props.rangeSelected)
        : props.rangeSelected;
    if (props.selectedTopics.length == 0) {
      let sum = 0;
      let count = 0;
      props.topics.forEach(item => {
        sum += item.skillLevel;
        count++;
      });

      if (count != 0 && sum / count <= 10) {
        const dataPoints = getDataPoints(
          'total',
          range,
          Math.round(sum / count),
        );
        chartData.push(dataSet(dataPoints, '#D5ACF3', 'Total'));
        haveValidDataToPlot = true;
      }
    } else {
      props.selectedTopics.forEach(item => {
        const scoreSelected = props.topics.find(topic => topic.id == item.id)
          ?.skillLevel;
        const dataPoints = getDataPoints(item.id, range, scoreSelected ?? 0);
        chartData.push(dataSet(dataPoints, item.color, item.label));
      });
      haveValidDataToPlot = true;
    }
  }

  return (
    <div className={'relative'}>
      <Line
        options={options}
        datasetIdKey={'id'}
        height={'160px'}
        data={{
          labels: props.xAxisLabel,
          datasets: chartData,
        }}
      />
      {!haveValidDataToPlot && !props.isLoading ? <NotAvailableState /> : null}
    </div>
  );
};

export default LineChartMasteryLevel;
